/* ============================================================
   Wiredan Vault Protocol - API Utility Fix (Token Key Sync & Connection Fix)
   Path: src/utils/api.js
   Status: HARDENED & BUG-FIXED
   ============================================================ */

// Replace this with your actual Cloudflare Worker URL for notifications or keep empty to disable safely
const MAILER_WORKER_URL = '';

// FIX: Robust fallback handling for API base URL resolution
const API_BASE = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || 
                 (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:8787/api' : '/api');

/**
 * Retrieves the JWT token from local storage.
 * Synchronized with AuthContext standards.
 */
export const getToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('agritrade_token') || 
         localStorage.getItem('wiredan_token') || 
         localStorage.getItem('token') || 
         null;
};

/**
 * Retrieves stored user identification headers for secure Omni-Engine validation.
 */
export const getAuthHeaders = () => {
  const token = getToken();
  
  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem('agritrade_user') || '{}');
  } catch (e) {}

  const userId = localStorage.getItem('userId') || storedUser?.id || storedUser?.uuid || '';
  const uuid = localStorage.getItem('userUuid') || localStorage.getItem('uuid') || storedUser?.uuid || storedUser?.id || '';

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(userId && { 'X-User-Id': userId }),
    ...(uuid && { 'X-UUID': uuid }),
  };
};

/**
 * Global request wrapper to handle headers, authorization, and errors safely.
 */
async function request(endpoint, options = {}) {
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const targetUrl = `${API_BASE}${formattedEndpoint}`;

  try {
    const res = await fetch(targetUrl, {
      ...options,
      headers,
    });

    const data = await res.json().catch(() => ({}));
    
    if (!res.ok || (data && data.success === false)) {
      // Prevent 401 response from wiping out valid local states instantly
      if (res.status === 401) {
        console.warn("API Warning: Received 401 Unauthorized for endpoint:", formattedEndpoint);
      }
      throw new Error(data.error || data.message || `API error (${res.status})`);
    }

    return data;
  } catch (err) {
    console.error(`API Request Failed [${formattedEndpoint}]:`, err.message);
    throw err;
  }
}

const api = {
  getToken,
  
  // --- NOTIFICATION POLLING (SAFE SHORT-CIRCUIT) ---
  checkNotifications: async (userId) => {
    if (!userId || !MAILER_WORKER_URL || MAILER_WORKER_URL === '') {
      return { success: true, count: 0 };
    }
    try {
      const res = await fetch(`${MAILER_WORKER_URL}/check-updates?userId=${userId}`);
      if (!res.ok) return { success: true, count: 0 };
      return await res.json();
    } catch (err) {
      return { success: true, count: 0 };
    }
  },

  // --- TOKEN DEX, LIVE PRICE & CHAT ENDPOINTS ---
  getTokenPrices: () => request('/tokens/prices', { method: 'GET' }),

  getTokenDetail: (contractAddress) => 
    request(`/public/token-details/${contractAddress.toLowerCase()}`, { method: 'GET' }),

  getChatHistory: (roomAddress) => 
    request(`/chat/history?room=${roomAddress.toLowerCase()}`, { method: 'GET' }),

  // Auth Endpoints
  login: (data) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  signup: (data) =>
    request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // VAULT & ESCROW
  confirmPayment: (data) => 
    request('/vault/escrow', {
      method: 'POST',
      body: JSON.stringify({ ...data, action: 'confirm_payment' }),
    }),

  syncOrder: (orderId) => 
    request(`/vault/sync?orderId=${orderId}&t=${Date.now()}`, {
      method: 'GET'
    }),

  initVault: (data) =>
    request('/vault/escrow', {
      method: 'POST',
      body: JSON.stringify({ ...data, action: 'init_vault' }),
    }),

  // Biometric & KYC Endpoints
  submitFaceVerification: (data) =>
    request('/kyc/verify-face', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Password Management
  forgotPassword: (data) =>
    request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  resetPassword: (data) =>
    request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Clears user session data and redirects to login.
   */
  logout: () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('agritrade_token');
    localStorage.removeItem('agritrade_user');
    localStorage.removeItem('wiredan_token');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('userUuid');
    localStorage.removeItem('uuid');
    window.location.href = '/login';
  }
};

export default api;